import { IFacetOption } from 'shared/types/ConstructorFacet';
import { removeDoubleQuotes } from 'search/utils/removeDoubleQuotes';

/**
 * [getYearRangeFromBucket] returns the slider range option from the bucket options
 * We need to do this because if were to do a filter on `release_date_year_range`
 * then that would decrease the options we get back from `release_date_year_bucket`
 * and we don't want to be limited so that the user can update the scope of filter
 * to include more values
 * @param options
 */

export const getYearRangeFromBucket = (options: Array<IFacetOption>): [number, number] => {
  const firstOption = options[0]?.display_name;
  const lastOption = options[options?.length - 1]?.display_name;

  const parsedYearMin = Number(firstOption?.split(' ')?.[0]);
  const parsedYearMax = Number(lastOption?.split(' ')?.[0]);

  if (parsedYearMin && parsedYearMax) {
    return [parsedYearMin, parsedYearMax];
  }

  // return a default value
  return [1985, new Date().getFullYear()];
};

/**
 * [transformBucketValueToRange]
 * @param arr ex: ['1990-2000']
 * @param isUpcomingReleasesSelected boolean
 * returns [1990, 2000]
 */
export const transformBucketValueToRange = (
  arr: Array<string>,
  isUpcomingReleasesSelected: boolean,
): [number, number] => {
  const currentYear = new Date().getFullYear();

  if (isUpcomingReleasesSelected) {
    return [currentYear, currentYear];
  }

  const str = arr?.[0];
  if (!str) return;

  const parsedValues = str.split('-');

  // 1985 and earlier is selected ('-inf-1985')
  if (parsedValues.length === 3 && parsedValues[1] === 'inf') {
    return [Number(parsedValues[2]), Number(parsedValues[2])];
  }

  // {current year} and later is selected ('2022-inf')
  if (parsedValues.length === 2 && parsedValues[1] === 'inf') {
    return [Number(parsedValues[0]), currentYear];
  }

  if (parsedValues?.length !== 2) return;

  return [Number(parsedValues[0]), Number(parsedValues[1])];
};

export const transformArrayToYearBucketValue = (arr: [number, number]): string => {
  if (arr.length !== 2) return;
  const currentYear = new Date().getFullYear();

  if (arr[1] === currentYear) {
    return `${arr[0]}-inf`;
  }

  return `${arr[0]}-${arr[1]}`;
};

/**
 * [shouldShowPlusSign] if the upper bound is 'inf' we need to show the + sign
 * @param arr (eg: ['2010-inf'])
 * @param isUpcomingReleasesSelected boolean
 */
export const shouldShowPlusSign = (arr: Array<string>, isUpcomingReleasesSelected) => {
  if (isUpcomingReleasesSelected) {
    return true;
  }

  const str = arr?.[0];

  if (!str) {
    // when no filters are applied, we want default to show +
    return true;
  }

  const parsedValues = str.split('-');

  return parsedValues[1] === 'inf';
};

export const getInitialYearBucketState = (facet) =>
  facet?.options
    ?.filter((option) => option.status === 'selected')
    ?.map((option) => removeDoubleQuotes(option.value));
